import type { ComponentType, ElementType } from 'react';

const MIN_WIDTH = 240;
const DEFAULT_WIDTH = 360;
const COLLAPSED_WIDTH = 56;
const MAX_WIDTH_RATIO = 0.8;
/** Минимальная ширина области Main; панель не может сжимать контент ниже этого значения */
const MAIN_MIN_WIDTH = 640;
const STORAGE_KEY = 'aya-side-panel';
const COLLAPSE_OVERRUN_PX = 100;
const REOPEN_OVERRUN_REDUCTION_PX = 30;

function clampWidth(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }

  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
}

/**
 * Док-элемент боковой панели.
 */
interface SidePanelDockItem {
  id: string;
  label: string;
  icon: ElementType;
  component: ComponentType;
  badge?: ComponentType;
  order: number;
  mode: 'panel' | 'fullscreen';
}

interface SidePanelState {
  opened: boolean;
  width: number;
  viewportWidth: number;
  navbarWidth: number;
  storedWidth: number;
  collapseReason: 'auto' | 'manual' | null;
  isResizing: boolean;
  dockItems: SidePanelDockItem[];
  activeDockId: string | null;
}

type Listener = () => void;

/**
 * Контроллер боковой панели управляет состоянием открытия и ширины панели.
 * Используется совместно с `useSyncExternalStore` для реагирования UI на изменения.
 */
class SidePanelController {
  private static instance: SidePanelController | null = null;

  private listeners = new Set<Listener>();

  private state: SidePanelState = {
    opened: false,
    width: DEFAULT_WIDTH,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
    navbarWidth: 240,
    storedWidth: DEFAULT_WIDTH,
    collapseReason: null,
    isResizing: false,
    dockItems: [],
    activeDockId: null,
  };

  private constructor() {
    this.restore();
  }

  /**
   * Возвращает синглтон контроллера, создавая его при первом обращении.
   */
  static getInstance(): SidePanelController {
    if (!SidePanelController.instance) {
      SidePanelController.instance = new SidePanelController();
    }

    return SidePanelController.instance;
  }

  /**
   * Подписывает слушателя на изменения состояния.
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Возвращает текущий снимок состояния.
   */
  getSnapshot = (): SidePanelState => this.state;

  /**
   * Регистрирует док-элемент, доступный для отображения в панели.
   * Повторный вызов с тем же `id` обновляет существующую запись.
   */
  registerDockItem(config: {
    id: string;
    label: string;
    icon: ElementType;
    component: ComponentType;
    badge?: ComponentType;
    order?: number;
    mode?: 'panel' | 'fullscreen';
  }): void {
    const order = Number.isFinite(config.order)
      ? (config.order as number)
      : Number.MAX_SAFE_INTEGER;
    const dockItem: SidePanelDockItem = {
      id: config.id,
      label: config.label,
      icon: config.icon,
      component: config.component,
      badge: config.badge,
      order,
      mode: config.mode ?? 'panel',
    };

    const withoutCurrent = this.state.dockItems.filter(item => item.id !== config.id);
    const dockItems = [...withoutCurrent, dockItem].sort((a, b) => a.order - b.order);

    this.updateState({
      dockItems,
    });
  }

  /**
   * Удаляет док-элемент и сбрасывает активное состояние при необходимости.
   */
  unregisterDockItem(id: string): void {
    const dockItems = this.state.dockItems.filter(item => item.id !== id);
    const activeDockId = this.state.activeDockId === id ? null : this.state.activeDockId;

    this.updateState({
      dockItems,
      activeDockId,
    });
  }

  /**
   * Активирует док-элемент и при необходимости раскрывает панель.
   */
  activateDockItem(id: string): void {
    const exists = this.state.dockItems.some(item => item.id === id);
    if (!exists) {
      return;
    }

    this.updateState({
      activeDockId: id,
    });

    this.ensurePanelOpened();
  }

  /**
   * Возвращает активный док-элемент, если он выбран.
   */
  getActiveDockItem(): SidePanelDockItem | null {
    if (!this.state.activeDockId) {
      return null;
    }

    return this.state.dockItems.find(item => item.id === this.state.activeDockId) ?? null;
  }

  /**
   * Открывает панель с сохранением текущей ширины.
   */
  openPanel(): void {
    const limits = this.getWidthLimits();
    const desiredWidth =
      this.state.collapseReason === 'auto' ? DEFAULT_WIDTH : this.state.storedWidth;
    const widthToRestore = clampWidth(desiredWidth, limits.min, limits.max);

    this.updateState({
      opened: true,
      width: widthToRestore,
      collapseReason: null,
    });
  }

  /**
   * Закрывает панель, сохраняя ширину.
   */
  closePanel(): void {
    this.updateState({
      opened: false,
      collapseReason: 'manual',
      isResizing: false,
      activeDockId: null,
    });
  }

  /**
   * Переключает состояние панели.
   */
  togglePanel(): void {
    if (this.state.opened) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  /**
   * Устанавливает новую ширину панели с учетом динамических ограничений.
   * При достижении минимальной ширины панель автоматически сворачивается.
   */
  setPanelWidth(nextWidth: number): void {
    const limits = this.getWidthLimits();
    const clampedWidth = clampWidth(nextWidth, limits.min, limits.max);
    const isAtMin = clampedWidth <= limits.min;
    const overshoot = limits.min - nextWidth;

    if (this.state.isResizing) {
      if (!this.state.opened) {
        const remainingOvershoot = limits.min - nextWidth;
        const reopenOvershootThreshold = Math.max(
          0,
          COLLAPSE_OVERRUN_PX - REOPEN_OVERRUN_REDUCTION_PX
        );

        if (remainingOvershoot <= reopenOvershootThreshold) {
          const restoredWidth = clampWidth(nextWidth, limits.min, limits.max);

          this.updateState({
            opened: true,
            width: restoredWidth,
            storedWidth: restoredWidth,
            collapseReason: null,
          });
        } else {
          this.updateState({
            width: limits.min,
          });
        }

        return;
      }

      if (this.state.opened && overshoot >= COLLAPSE_OVERRUN_PX) {
        this.updateState({
          opened: false,
          width: limits.min,
          collapseReason: 'auto',
          storedWidth: DEFAULT_WIDTH,
          activeDockId: null,
        });
        return;
      }

      this.updateState({
        width: clampedWidth,
        storedWidth: isAtMin ? this.state.storedWidth : clampedWidth,
      });
      return;
    }

    if (isAtMin) {
      this.updateState({
        opened: false,
        width: limits.min,
        collapseReason: 'auto',
        storedWidth: DEFAULT_WIDTH,
        activeDockId: null,
      });
      return;
    }

    this.updateState({
      width: clampedWidth,
      storedWidth: clampedWidth,
      collapseReason: null,
    });
  }

  /**
   * Фиксирует ширину панели в свернутом режиме.
   */
  collapsePanel(): void {
    this.updateState({
      opened: false,
      collapseReason: 'manual',
      isResizing: false,
      activeDockId: null,
    });
  }

  /**
   * Завершает ручной ресайз и принимает решение о сворачивании.
   */
  finalizeResize(): void {
    if (!this.state.isResizing) {
      return;
    }

    if (!this.state.opened) {
      this.updateState({
        isResizing: false,
      });
      return;
    }

    this.updateState({
      storedWidth: this.state.width,
      collapseReason: null,
      isResizing: false,
    });
  }

  /**
   * Помечает начало ручного ресайза.
   */
  beginResize(): void {
    if (!this.state.opened) {
      return;
    }

    this.updateState({ isResizing: true });
  }

  /**
   * Обновляет ширину viewport для пересчета динамического максимума.
   * При переходе с мобильного на десктоп (viewport >= 48em) и открытой панели
   * восстанавливаем отображаемую ширину из storedWidth, иначе на десктопе остаётся 240.
   */
  setViewportWidth(viewportWidth: number): void {
    const limits = this.computeWidthLimits({ viewportWidth });
    const wasMobile = this.state.viewportWidth < 768;
    const isDesktop = viewportWidth >= 768;
    const shouldRestoreStoredWidth = this.state.opened && wasMobile && isDesktop;
    const width = shouldRestoreStoredWidth
      ? clampWidth(this.state.storedWidth, limits.min, limits.max)
      : clampWidth(this.state.width, limits.min, limits.max);

    this.updateState({
      viewportWidth,
      width,
    });
  }

  /**
   * Устанавливает ширину Navbar, чтобы учесть её при расчете максимальной ширины панели.
   */
  setNavbarWidth(navbarWidth: number): void {
    const limits = this.computeWidthLimits({ navbarWidth });
    const width = clampWidth(this.state.width, limits.min, limits.max);

    this.updateState({
      navbarWidth,
      width,
    });
  }

  /**
   * Возвращает текущие ограничения ширины.
   */
  getWidthLimits() {
    return this.computeWidthLimits();
  }

  private computeWidthLimits(
    next?: Partial<Pick<SidePanelState, 'viewportWidth' | 'navbarWidth'>>
  ) {
    const viewportWidth = next?.viewportWidth ?? this.state.viewportWidth;
    const navbarWidth = next?.navbarWidth ?? this.state.navbarWidth;

    const availableWidth = Math.max(0, viewportWidth - navbarWidth);
    const dynamicMax = Math.floor(availableWidth * MAX_WIDTH_RATIO);
    const maxFromMainMin = Math.max(0, availableWidth - MAIN_MIN_WIDTH);
    const max = Math.max(MIN_WIDTH, Math.min(dynamicMax, maxFromMainMin));

    return { min: MIN_WIDTH, max, collapsed: COLLAPSED_WIDTH };
  }

  /**
   * Проверяет, является ли устройство мобильным.
   * Использует ту же логику, что и useDevice: проверка через media query.
   * Mantine breakpoint 'sm' = 48em = 768px (по умолчанию).
   */
  private isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;
    // Используем ту же логику, что и useDevice
    // isMobile = !(min-width: 48em)
    return !window.matchMedia('(min-width: 48em)').matches;
  }

  /**
   * Уведомляет подписчиков об изменении состояния.
   */
  private emit(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Обновляет состояние и эмитит событие, если данные изменились.
   */
  private updateState(patch: Partial<SidePanelState>): void {
    const previous = this.state;
    const next: SidePanelState = {
      ...previous,
      ...patch,
    };

    const stateChanged =
      previous.opened !== next.opened ||
      previous.width !== next.width ||
      previous.viewportWidth !== next.viewportWidth ||
      previous.navbarWidth !== next.navbarWidth ||
      previous.storedWidth !== next.storedWidth ||
      previous.collapseReason !== next.collapseReason ||
      previous.isResizing !== next.isResizing ||
      previous.activeDockId !== next.activeDockId ||
      previous.dockItems !== next.dockItems;

    if (!stateChanged) {
      return;
    }

    this.state = next;
    // Не пишем в localStorage во время активного перетаскивания — это синхронная операция и даёт лаги
    if (!this.state.isResizing) {
      this.persist();
    }
    this.emit();
  }

  private persist(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // На мобильных устройствах вообще не сохраняем состояние панели
    if (this.isMobileDevice()) {
      return;
    }

    try {
      const payload = {
        opened: this.state.opened,
        width: this.state.width,
        storedWidth: this.state.storedWidth,
        collapseReason: this.state.collapseReason,
        activeDockId: this.state.activeDockId,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* noop */
    }
  }

  private restore(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as Partial<
        Pick<SidePanelState, 'opened' | 'width' | 'storedWidth' | 'collapseReason' | 'activeDockId'>
      >;

      const limits = this.getWidthLimits();
      const restoredStoredWidth = parsed.storedWidth ?? parsed.width ?? DEFAULT_WIDTH;
      const width = clampWidth(restoredStoredWidth, limits.min, limits.max);

      this.state = {
        ...this.state,
        opened: parsed.opened ?? this.state.opened,
        width,
        storedWidth: restoredStoredWidth,
        collapseReason: parsed.collapseReason ?? null,
        activeDockId: parsed.activeDockId ?? null,
      };
    } catch {
      /* noop */
    }
  }

  /**
   * Гарантирует, что панель раскрыта, сохраняя текущие ограничения.
   */
  private ensurePanelOpened(): void {
    if (this.state.opened) {
      return;
    }

    this.openPanel();
  }
}

export { SidePanelController, MIN_WIDTH, COLLAPSED_WIDTH };
export type { SidePanelDockItem };
